<template>
  <UiDialog @update:open="onUpdateOpen">
    <UiDialogContent :class="`max-w-${currentStep?.maxWidth}`">
      <UiDialogHeader>
        <IconStreamlabs v-if="isSEImportStep" class="w-56 ml-60 mb-16" />
        <UiDialogTitle> {{ currentStep?.title }} </UiDialogTitle>
        <UiDialogDescription v-if="currentStep?.description">
          {{ currentStep?.description }}
        </UiDialogDescription>
      </UiDialogHeader>

      <div
        :class="`flex h-[${currentStep?.maxHeight || 614}px] items-center justify-center`"
      >
        <DialogOnboardingStreamLabels
          v-if="currentStepId === Step.StreamLabels"
          v-model:import-stream-labels-enabled="importStreamLabelsEnabled"
        />

        <DialogOnboardingCloudbotStep
          v-if="currentStepId === Step.Cloudbot"
          v-model:cloudbot-enabled="cloudbotEnabled"
        />

        <DialogOnboardingTippingStep
          v-else-if="currentStepId === Step.Tipping"
          v-model:paypal-email="paypalEmail"
        />
        <DialogOnboardingOverlayStep
          v-else-if="currentStepId === Step.Overlay"
          v-model:selectedOverlayId="selectedOverlayId"
          :overlays="overlays"
        />
        <DialogOnboardingUltraStep v-else-if="currentStepId === Step.Ultra" />
        <DialogOnboardingSEImport
          v-else-if="currentStepId === Step.SEImport"
          :doContinue="doContinue"
        />
      </div>

      <!-- hide the footer if we're showing the login panel -->
      <DialogFooter v-if="auth.isAuthenticated">
        <!-- if there is a prev step, show Back button-->
        <Button
          v-if="currentStepIdx > 0"
          variant="neutral"
          class="sm:mr-auto"
          @click="currentStepIdx -= 1"
        >
          {{ $t('Back') }}
        </Button>

        <!-- if there is a next step, show Skip button, except on Ultra and SE Import step-->
        <Button
          v-if="
            currentStepIdx < stepOrder.length - 1 &&
            !isUltraStep &&
            !isSEImportStep
          "
          variant="link"
          @click="doSkip"
        >
          {{ $t('Skip') }}
        </Button>

        <!-- if there is a next step, show Continue button-->
        <Button
          v-if="currentStepIdx < stepOrder.length - 1"
          :busy="busy"
          @click="doContinue"
          :variant="isSEImportStep ? 'link' : undefined"
        >
          {{ continueLabel }}
        </Button>

        <!-- in the last step (themes), Browse Overlays acts as skip -->
        <template v-else>
          <Button
            variant="link"
            @click="doSkipThemeInstallAndCompleteOnboarding"
          >
            {{ $t('Browse All Overlays') }}
          </Button>

          <Button @click="completeOnboarding" :busy="busy">
            {{ $t('Install') }}
          </Button>
        </template>
      </DialogFooter>
    </UiDialogContent>
  </UiDialog>
</template>

<script setup lang="ts">
import { sleep } from '~/lib/util';
import streamlabs from '~/lib/streamlabs-api';

const { t: $t } = useI18n();
const { track: $track } = useTrackingStore();

enum Step {
  Overlay,
  Tipping,
  Cloudbot,
  Ultra,
  StreamLabels,
  SEImport,
}

const emit = defineEmits(['cancel']);

const busy = ref(false);

const steps: Record<
  Step,
  {
    title?: string;
    description?: string;
    maxHeight?: string;
    maxWidth?:
      | '4xl'
      | 'lg'
      | 'xs'
      | 'sm'
      | 'md'
      | 'xl'
      | '2xl'
      | '3xl'
      | '5xl'
      | '6xl'
      | '7xl'
      | undefined;
    doContinue?: () => void | Promise<void>;
    doSkip?: () => void | Promise<void>;
  }
> = {
  [Step.StreamLabels]: {
    title: $t('Import Your Stream Labels'),
    description: $t(
      `We've detected that you're using some Stream Labels. Let's import them!`,
    ),
    maxWidth: '3xl',
    doContinue: async () => {
      if (
        importStreamLabelsEnabled.value &&
        streamLabels.hasImportableStreamLabels
      ) {
        busy.value = true;
        await streamLabels.doImportStreamLabels();

        // the above doesn't seem to be async, so let's wait til there are
        // no labels to import
        while (streamLabels.hasImportableStreamLabels) {
          await sleep(100);
        }
        busy.value = false;
      }

      currentStepIdx.value += 1;
    },
  },
  [Step.Overlay]: {
    title: $t('Add an Overlay'),
    description: $t(
      `Professionally designed overlays to make your stream shine.`,
    ),
    maxWidth: '3xl',
    doSkip() {
      selectedOverlayId.value = undefined;
      currentStepIdx.value += 1;
    },
  },

  [Step.Cloudbot]: {
    title: $t('Set Up Cloudbot'),
    description: $t(
      `Handle entertainment and moderation for your stream with our cloud-based chatbot`,
    ),
    maxWidth: '3xl',
  },

  // todo: skip this step if the user has tipping setup already
  [Step.Tipping]: {
    title: $t('Set Up Tipping'),
    description: $t(`Accept tips and engage with your audience`),
    maxWidth: '3xl',
  },

  [Step.Ultra]: {
    title: $t('Choose Your Plan'),
    maxWidth: '4xl',
    doContinue: () => {
      if (!auth.isUltra) {
        $track('onboarding_free');
      }
      currentStepIdx.value += 1;
    },
  },
  [Step.SEImport]: {
    title: $t('Import your StreamElements settings'),
    description: $t(
      'Import your overlays, widgets, tip settings, chatbot settings, and loyalty points.',
    ),
    maxWidth: '3xl',
    maxHeight: '314',
  },
};

const stepOrder = computed(() => {
  const steps = [Step.Overlay];

  if (
    streamLabels.hasHasImportableStreamLabels ||
    streamLabels.hasImportableStreamLabels
  ) {
    console.debug(
      'streamlabs.hasHasImportableStreamLabels',
      streamLabels.hasHasImportableStreamLabels,
    );
    steps.unshift(Step.StreamLabels);
  }

  if (!auth.isUltra) {
    steps.unshift(Step.Ultra);
  }

  if (!!library.SEToken) {
    steps.unshift(Step.SEImport);
  }

  return steps;
});

const currentStepIdx = ref(0);

const currentStepId = computed(() => stepOrder.value[currentStepIdx.value]);
const currentStep = computed(() => steps[currentStepId.value || Step.Overlay]);

const doContinue = async () => {
  if (currentStep.value.doContinue) {
    await currentStep.value.doContinue();
  } else {
    currentStepIdx.value += 1;
  }
};

const doSkip = async () => {
  if (currentStep.value.doSkip) {
    await currentStep.value.doSkip();
  } else {
    currentStepIdx.value += 1;
  }
};

const doSkipThemeInstallAndCompleteOnboarding = async () => {
  await doSkip();
  await completeOnboarding();
  navigateTo('/library');
};

const selectedOverlayId = ref();
const paypalEmail = ref('');
const cloudbotEnabled = ref(true);
const importStreamLabelsEnabled = ref(true);

const auth = useAuthStore();
const app = useAppStore();
const streamLabels = useStreamLabelsStore();

const library = useLibraryStore();

const cancelOnboarding = () => {
  $track('onboarding_canceled');
  emit('cancel');
};

const browse = streamlabs.fetchOnboardingThemes();

const overlays = computed(() => {
  if (browse.data.value) {
    return browse.data.value.data.filter((t: { is_free: boolean }) => {
      return t.is_free === !auth.isUltra;
    });
  }

  return Array.apply(null, Array(5)).map((x) => null);
});

const completeOnboarding = async () => {
  if (selectedOverlayId.value) {
    busy.value = true;
    await library
      .fetchItem(selectedOverlayId.value)
      .then((r) => library.installOverlay(r.data as TsThemesItemOverlay));
    busy.value = false;
  }

  app.onboardingComplete = true;
};

const onUpdateOpen = (open: boolean) => {
  if (!open) {
    cancelOnboarding();
  }
};

watch(
  () => app.onboardingComplete,
  (onboardingComplete) => {
    if (!onboardingComplete) {
      currentStepIdx.value = 0;
      setDefaultSelectedOverlay();
    }
  },
);

// TODO: without this, selected overlay is null on fresh launch
// Much better ways to do this
watch(overlays, (val, prev) => {
  if (prev.every((x) => x === null) && val.every((x) => x !== null)) {
    setDefaultSelectedOverlay();
  }
});

const setDefaultSelectedOverlay = () => {
  selectedOverlayId.value = overlays.value[2]?.id;
};

const isUltraStep = computed(() => currentStepId.value === Step.Ultra);
const isSEImportStep = computed(() => currentStepId.value === Step.SEImport);

watch(
  () => auth.isUltra,
  async (isUltra, wasUltra) => {
    if (isUltra && wasUltra === false && isUltraStep.value) {
      await sleep(1000);
      setDefaultSelectedOverlay();
      doContinue();
    }
  },
);

const continueLabel = computed(() => {
  if (isSEImportStep) return $t('Continue without Import');
  if (isUltraStep) return $t('Continue with Free');
  return $t('Continue');
});
</script>
